// Sonde de bascule d'onglet.
//
// Objectif : savoir POURQUOI un changement d'onglet est ressenti comme lent,
// au lieu de le deviner. On ne peut pas mesurer ça depuis un poste de dev :
// le lag est un phénomène de WebView sur device. Cette sonde s'active donc
// aussi sur un build de production, à la demande.
//
//   Activer  : localStorage.setItem('harmonia:tabprobe', '1') puis relancer
//   Désactiver : localStorage.removeItem('harmonia:tabprobe')
//
// Elle imprime une ligne par bascule, par exemple :
//   [tab] tasks → stats | 1re frame 34ms | stable 412ms | frames perdues 7 (pire 118ms) | requêtes 3
//
// Comment lire :
// - « 1re frame » : délai avant la toute première image après le tap. Si c'est
//   haut, quelque chose bloque le thread principal AVANT même de peindre.
// - « stable » : moment où le rythme redevient régulier. C'est la durée réelle
//   ressentie comme « lag ».
// - « frames perdues » : nombre d'images sautées. C'est ça, la saccade.
// - « requêtes » : appels réseau partis pendant la fenêtre. S'il y en a et que
//   la page était déjà montée, alors on refetch sur une page déjà peinte.
//
// Volontairement sans dépendance et sans PerformanceObserver('longtask'),
// non supporté par WKWebView. Le rythme des rAF est un proxy de jank portable.

const FLAG = 'harmonia:tabprobe'
const WINDOW_MS = 1500
/** Au-delà, une frame est considérée perdue (60fps ≈ 16.7ms, on tolère 2 frames). */
const DROPPED_FRAME_MS = 34
/** Nombre de frames consécutives dans le budget pour déclarer le rythme stable. */
const STABLE_STREAK = 8

export function isTabProbeEnabled(): boolean {
  try {
    return localStorage.getItem(FLAG) === '1'
  } catch {
    return false
  }
}

function countRequests(sinceMs: number): number {
  try {
    return performance
      .getEntriesByType('resource')
      .filter((e) => e.startTime >= sinceMs && /xmlhttprequest|fetch/.test((e as PerformanceResourceTiming).initiatorType))
      .length
  } catch {
    return -1
  }
}

let running = false

/**
 * À appeler au moment du tap, avant que la navigation ne parte.
 * No-op si la sonde est désactivée ou si une mesure est déjà en cours.
 */
export function probeTabSwitch(from: string, to: string): void {
  if (!isTabProbeEnabled() || running) return
  running = true

  const t0 = performance.now()
  let firstFrame = -1
  let stableAt = -1
  let dropped = 0
  let worst = 0
  let streak = 0
  let prev = t0

  const tick = () => {
    const now = performance.now()
    const gap = now - prev
    prev = now

    if (firstFrame < 0) {
      firstFrame = Math.round(gap)
    } else {
      if (gap > DROPPED_FRAME_MS) {
        dropped++
        worst = Math.max(worst, gap)
        streak = 0
      } else if (stableAt < 0 && ++streak >= STABLE_STREAK) {
        stableAt = Math.round(now - t0)
      }
    }

    if (now - t0 < WINDOW_MS) {
      requestAnimationFrame(tick)
      return
    }

    running = false
    const requests = countRequests(t0)
    console.log(
      `[tab] ${from} → ${to}` +
        ` | 1re frame ${firstFrame}ms` +
        ` | stable ${stableAt < 0 ? '>' + WINDOW_MS : stableAt}ms` +
        ` | frames perdues ${dropped}${dropped ? ` (pire ${Math.round(worst)}ms)` : ''}` +
        ` | requêtes ${requests < 0 ? 'n/c' : requests}`
    )
  }

  requestAnimationFrame(tick)
}
